import { Asset } from 'stac-js';

const TITLE = "EOCube.ro";

const CATALOG_URL = "https://stac.eocube.ro";

// EOCube/UVT object-storage HTTPS gateway (SeaweedFS S3). Asset hrefs are stored
// as s3://<bucket>/<key>, which a browser cannot open; this is the HTTPS base used
// to turn them into directly downloadable links ({platform}/{bucket}/{key}).
const EOCUBE_S3_PLATFORM = "https://storage.svc.uvt-01.eocube.ro";

// Convert s3://<bucket>/<key> to an HTTPS URL on the given platform; null if not s3.
function s3ToHttps(href, platform) {
  const m = /^s3:\/\/([^/]+)\/(.+)$/.exec(typeof href === "string" ? href : "");
  return m ? `${platform.replace(/\/+$/, "")}/${m[1]}/${m[2]}` : null;
}

// Resolve the EOCube/UVT storage platform from the item's `storage:scheme`
// (STAC storage extension), falling back to the known gateway for collections
// that don't declare it yet (e.g. sentinel-1-slc).
function eocubePlatform(stac) {
  const schemes = stac && stac.properties && stac.properties["storage:scheme"];
  if (schemes && typeof schemes === "object") {
    for (const scheme of Object.values(schemes)) {
      const platform = scheme && scheme.platform;
      if (typeof platform === "string" && /eocube|uvt/i.test(platform)) {
        return platform;
      }
    }
  }
  return EOCUBE_S3_PLATFORM;
}

export default {
  catalogUrl: CATALOG_URL,
  catalogTitle: TITLE,
  catalogTitleAfterImage: null,
  catalogImage: null,
  allowExternalAccess: false, // Must be true if catalogUrl is not given
  allowedDomains: [
    "eocube.ro",
    "storage.svc.uvt-01.eocube.ro", // S3 gateway — allow auth creds on (future) private assets
    "localhost"
  ],
  enforcedColorMode: "auto",
  detectLocaleFromBrowser: true,
  storeLocale: true,
  locale: "ro",
  fallbackLocale: "en", // upstream's ro texts miss strings; en has every one, so nothing renders as a raw key
  supportedLocales: [
    "de",
    "es",
    "en",
    "fr",
    "it",
    "pt",
    "ro"
  ],
  apiCatalogPriority: null,
  useTileLayerAsFallback: false,
  displayGeoTiffByDefault: false,
  displayPreview: true,
  displayOverview: true,
  displayOverviewsForChildren: false,
  maxDisplayPixels: null,
  buildTileUrlTemplate: null,
  getMapSourceOptions: null,
  getStacLayerOptions: null,
  pathPrefix: "/",
  historyMode: "history",
  cardViewMode: "cards",
  showFavorites: true,
  defaultCollectionSort: "title",
  defaultItemSort: null,
  showKeywordsInItemCards: false,
  showKeywordsInCatalogCards: false,
  preferredAssets: true,
  showThumbnailsAsAssets: false,
  searchResultsPerPage: null,
  itemsPerPage: null,
  collectionsPerPage: null,
  maxEntriesPerPage: 1000,
  defaultThumbnailSize: null,
  crossOriginMedia: null,
  requestHeaders: {},
  requestQueryParameters: {},
  socialSharing: ['email', 'bsky', 'mastodon', 'x'],
  preprocessSTAC: stac => {
    // Force the branded title on the root catalog (the API's own root title is
    // the stac-fastapi default). getBrowserPath() is gone in v5; compare the
    // entity's absolute URL against the configured catalog root instead.
    const url = typeof stac.getAbsoluteUrl === 'function' ? stac.getAbsoluteUrl() : null;
    if (typeof url === 'string' && url.replace(/\/+$/, "") === CATALOG_URL) {
      stac.title = TITLE;
    }
    // Make EOCube S3 assets directly downloadable: rewrite the s3://<bucket>/<key>
    // href to an HTTPS URL, while preserving the original s3:// path as an
    // alternate (so users can still copy-paste it into S3 tooling).
    const assets = stac && stac.assets;
    if (assets && typeof assets === 'object') {
      const platform = eocubePlatform(stac);
      for (const asset of Object.values(assets)) {
        if (!asset || typeof asset.href !== 'string' || !asset.href.startsWith('s3://')) {
          continue;
        }
        // A trailing-slash href is a dataset PREFIX (e.g. the hive-partitioned
        // analytics GeoParquet datasets on the burn-scars collection), not a
        // downloadable object — and those prefixes are registration-gated by
        // design. Leave it as s3:// so the browser renders a "Copy URL for S3"
        // button (for DuckDB/PyArrow/Polars) instead of an HTTPS download that
        // can only ever return AccessDenied. Don't add an HTTPS alternate
        // either: with preferredAssets enabled, Asset.vue auto-selects the
        // first HTTP(S) tab, which would resurface the broken link.
        if (asset.href.endsWith('/')) {
          continue;
        }
        const https = s3ToHttps(asset.href, platform);
        if (!https) {
          continue;
        }
        // Keep the original s3:// path as an alternate (alternate-assets extension)
        // so users can copy-paste it into S3 tooling. It must be a real stac-js
        // Asset instance (with the parent asset as context): Asset.vue calls
        // .fillAlternate() on every alternate, which a plain object doesn't have.
        // stac-js only auto-wraps `alternate` at construction time, and
        // preprocessSTAC runs after that.
        const s3Alternate = new Asset(
          { href: asset.href, 'alternate:name': 'S3' },
          's3',
          asset
        );
        asset.alternate = Object.assign({}, asset.alternate || {}, { s3: s3Alternate });
        asset.href = https;
      }
    }
    return stac;
  },
  authConfig: {
    type: 'openIdConnect',
    openIdConnectUrl: 'https://aai.eocube.ro/realms/rocs/.well-known/openid-configuration',
    oidcConfig: {
      client_id: 'stac-browser'
    }
  },
  transactions: 'auto',
  transactionsRequireLogin: true,
  transactionsRequirePreflight: true,
  crs: {},
  footerLinks: null
};
